import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { parseISO } from "date-fns";
import { Box, Typography, RadioGroup, FormControlLabel, FormControl, Divider } from "@mui/material";
import CityAutocomplete from "../../../Inputs/CityAutocomplete/CityAutocomplete";
import PrimaryDatePicker from "../../../Inputs/PrimaryDatePicker/PrimaryDatePicker";
import PrimaryRadio from "../../../Buttons/PrimaryRadio/PrimaryRadio";
import PrimaryTextField from "../../../Inputs/PrimaryTextField/PrimaryTextField";
import SecondaryButton from "../../../Buttons/SecondaryButton/SecondaryButton";
import PrimaryButton from "../../../Buttons/PrimaryButton/PrimaryButton";
import useEquipmentTypes from "../../../../hooks/useEquipmentTypes/useEquipmentTypes";
import { getDistance } from "../../../../utils/calcHelpers";
import { handleAltEnter } from "../../../../utils/keyboardShortcutHelpers";
import { CarrierService } from "../../../../services/CarrierService";
import { AlertContext } from "../../../Dashboard/Dashboard";

export default function PreferredLanesManual({
  action,
  userIdMcld,
  activeCarrier,
  existingLane,
  handleClose
}) {
  let today = new Date();
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const { typeLabels } = useEquipmentTypes();
  const [originCity, setOriginCity] = useState({});
  const [destinationCity, setDestinationCity] = useState({});
  const [startDate, setStartDate] = useState(new Date());
  const [expiryDate, setExpiryDate] = useState(new Date(today.setDate(today.getDate() + 365)));
  const [mode, setMode] = useState("V53");
  const [weekly, setWeekly] = useState("");
  const [availableSunday, setAvailableSunday] = useState("");
  const [availableMonday, setAvailableMonday] = useState("");
  const [availableTuesday, setAvailableTuesday] = useState("");
  const [availableWednesday, setAvailableWednesday] = useState("");
  const [availableThursday, setAvailableThursday] = useState("");
  const [availableFriday, setAvailableFriday] = useState("");
  const [availableSaturday, setAvailableSaturday] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [disabledWeekly, setDisabledWeekly] = useState(false);
  const [disabledDaily, setDisabledDaily] = useState(false);
  const [proposedCost, setProposedCost] = useState(null);
  const [ratePerMile, setRatePerMile] = useState(null);
  const [miles, setMiles] = useState(null);
  const [originCityDefault, setOriginCityDefault] = useState("");
  const [destinationCityDefault, setDestinationCityDefault] = useState("");

  const handleReset = () => {
    setOriginCity({});
    setDestinationCity({});
    setStartDate(new Date());
    setMode("V53");
    setWeekly("");
    setAvailableSunday("");
    setAvailableMonday("");
    setAvailableTuesday("");
    setAvailableWednesday("");
    setAvailableThursday("");
    setAvailableFriday("");
    setAvailableSaturday("");
  };

  const handleCancel = () => {
    handleReset();
    handleClose();
  };

  const handleModeChange = (event) => {
    setMode(event.target.value);
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (value) {
      let date = new Date(value.getTime());
      setExpiryDate(new Date(date.setFullYear(date.getFullYear() + 1)));
    } else {
      setExpiryDate(value);
    }
  };

  const handleSave = () => {
    setIsUpdating(true);
    let now = new Date();
    const payload = {
      carrierId: activeCarrier.id,
      originCity: originCity.name,
      originState: originCity.state,
      originCityId: originCity.id,
      destinationCity: destinationCity.name,
      destinationState: destinationCity.state,
      destinationCityId: destinationCity.id,
      startDate: startDate.toISOString(),
      weekly: weekly ? parseInt(weekly) : 0,
      postedDate: now.toISOString(),
      postedBy: userIdMcld,
      proposedCost: proposedCost || 0,
      proposedRpm: ratePerMile || 0,
      endDate: expiryDate.toISOString(),
      miles: miles,
      availableDaily: {
        availableMonday: availableMonday ? parseInt(availableMonday) : 0,
        availableTuesday: availableTuesday ? parseInt(availableTuesday) : 0,
        availableWednesday: availableWednesday ? parseInt(availableWednesday) : 0,
        availableThursday: availableThursday ? parseInt(availableThursday) : 0,
        availableFriday: availableFriday ? parseInt(availableFriday) : 0,
        availableSaturday: availableSaturday ? parseInt(availableSaturday) : 0,
        availableSunday: availableSunday ? parseInt(availableSunday) : 0,
      },
      equipmentTypeDaily: {
        equipmentTypeMonday: mode,
      }
    };

    if (action === "edit-capacity") {
      payload.id = existingLane.id;
      payload.updatedBy = userIdMcld;

      CarrierService.UpdatePreferredLane(instance, payload).then(() => {
        setIsUpdating(false);
        handleClose();
      }).catch(error => {
        console.error(error);
        setIsUpdating(false);
        warning(error);
      });
    } else {
      CarrierService.CreatePreferredLane(instance, payload).then(() => {
        setIsUpdating(false);
        handleClose();
      }).catch(error => {
        console.error(error);
        setIsUpdating(false);
        warning(error);
      });
    }
  };

  const weeklyInputs = [
    {
      label: "Sun",
      value: availableSunday,
      onChange: setAvailableSunday
    },
    {
      label: "Mon",
      value: availableMonday,
      onChange: setAvailableMonday
    },
    {
      label: "Tue",
      value: availableTuesday,
      onChange: setAvailableTuesday
    },
    {
      label: "Wed",
      value: availableWednesday,
      onChange: setAvailableWednesday
    },
    {
      label: "Thu",
      value: availableThursday,
      onChange: setAvailableThursday
    },
    {
      label: "Fri",
      value: availableFriday,
      onChange: setAvailableFriday
    },
    {
      label: "Sat",
      value: availableSaturday,
      onChange: setAvailableSaturday
    }
  ];

  useEffect(() => {
    setSubmitDisabled(
      !activeCarrier ||
      !originCity ||
      !destinationCity ||
      !startDate ||
      !expiryDate ||
      (!disabledDaily && !disabledWeekly)
    );
  }, [
    activeCarrier,
    originCity,
    destinationCity,
    startDate,
    expiryDate,
    disabledDaily,
    disabledWeekly
  ]);

  useEffect(() => {
    setDisabledDaily(!!weekly);
    setDisabledWeekly(
      !!availableSunday ||
      !!availableMonday ||
      !!availableTuesday ||
      !!availableWednesday ||
      !!availableThursday ||
      !!availableFriday ||
      !!availableSaturday
    );
  }, [
    weekly,
    availableSunday,
    availableMonday,
    availableTuesday,
    availableWednesday,
    availableThursday,
    availableFriday,
    availableSaturday,
  ]);

  useEffect(() => {
    if (originCity?.id && destinationCity?.id) {
      setMiles(
        getDistance(
          originCity.latitude,
          originCity.longitude,
          destinationCity.latitude,
          destinationCity.longitude
        )
      );
    } else {
      setMiles(null);
    }

  }, [originCity, destinationCity]);

  useEffect(() => {
    const setDailyWeekly = (daily) => {
      setWeekly(
        !daily?.availableSunday &&
          !daily?.availableMonday &&
          !daily?.availableTuesday &&
          !daily?.availableWednesday &&
          !daily?.availableThursday &&
          !daily?.availableFriday &&
          !daily?.availableSaturday
          ? existingLane?.weekly : "");
      setAvailableSunday(daily?.availableSunday);
      setAvailableMonday(daily?.availableMonday);
      setAvailableTuesday(daily?.availableTuesday);
      setAvailableWednesday(daily?.availableWednesday);
      setAvailableThursday(daily?.availableThursday);
      setAvailableFriday(daily?.availableFriday);
      setAvailableSaturday(daily?.availableSaturday);
    };

    if (existingLane) {
      const daily = existingLane.availableDaily;
      setDailyWeekly(daily);

      setStartDate(parseISO(existingLane.startDate));
      setExpiryDate(existingLane.endDate ? parseISO(existingLane.endDate) : new Date(today.setDate(today.getDate() + 365)));
      setMode(existingLane?.equipmentTypeDaily?.equipmentTypeMonday ?? "V53");
      setProposedCost(existingLane.proposedCost && !existingLane.proposedRpm ? existingLane.proposedCost : null);
      setRatePerMile(existingLane.proposedRpm ? existingLane.proposedRpm : null);
      setMiles(existingLane.miles);
      setOriginCityDefault(existingLane.originCity ? `${existingLane.originCity}, ${existingLane.originState}` : "");
      setDestinationCityDefault(existingLane.destinationCity ? `${existingLane.destinationCity}, ${existingLane.destinationState}` : "");
    }
  }, [existingLane]);

  return (
    <Box onKeyDown={(e) => handleAltEnter(e, (submitDisabled || isUpdating), handleSave)}>
      <Box className="Carrier-modal-input-row">
        <Typography variant="subhead2" color="black_solid.80">
          Locations
        </Typography>
      </Box>
      <Box className="Carrier-modal-input-row">
        <Box>
          <CityAutocomplete
            label="Origin *"
            updateSelectedCity={setOriginCity}
            defaultInput={originCityDefault}
            error={!originCity}
          />
        </Box>
        <Box sx={{ marginLeft: "20px" }}>
          <CityAutocomplete
            label="Destination *"
            updateSelectedCity={setDestinationCity}
            defaultInput={destinationCityDefault}
            error={!destinationCity}
          />
        </Box>
      </Box>
      <Box className="Carrier-modal-input-row" sx={{ alignItems: "center" }}>
        <Typography variant="subhead2" color="black_solid.80">
          Mode
        </Typography>
        <FormControl sx={{ margin: "2px 0 0 10px" }}>
          <RadioGroup
            row
            aria-labelledby="carrier-search-radio-buttons"
            name="row-radio-buttons-group"
            value={mode}
            onChange={handleModeChange}
          >
            {typeLabels.map(type => (
              <FormControlLabel
                key={`equipment-type-${type.id}`}
                value={type.id}
                control={<PrimaryRadio />}
                label={<Typography variant="body2" color="black_solid.60">{type.label}</Typography>}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
      <Divider sx={{ marginBottom: "16px" }} />
      <Box className="Carrier-modal-input-row">
        <Typography variant="subhead2" color="black_solid.80">
          Dates
        </Typography>
      </Box>
      <Box className="Carrier-modal-input-row">
        <Box>
          <PrimaryDatePicker
            label="Start Date *"
            value={startDate}
            handleChange={handleStartDateChange}
            width="260px"
            disablePast
            error={!startDate}
          />
        </Box>
        <Box sx={{ marginLeft: "20px" }}>
          <PrimaryDatePicker
            label="Expiry Date *"
            value={expiryDate}
            handleChange={setExpiryDate}
            width="260px"
            disablePast
            error={!expiryDate}
          />
        </Box>
      </Box>
      <Divider sx={{ marginBottom: "16px" }} />
      <Box className="Carrier-modal-input-row">
        <Typography variant="subhead2" color="black_solid.80">
          Costs
        </Typography>
      </Box>
      <Box className="Carrier-modal-input-row">
        <Box>
          <PrimaryTextField
            label="Proposed Cost"
            onChange={setProposedCost}
            width="120px"
            value={proposedCost}
            isNumber
            isCurrency
            maxLength="8"
            disabled={!!ratePerMile}
          />
        </Box>
        <Box sx={{ marginLeft: "20px" }}>
          <PrimaryTextField
            label="Rate Per Mile"
            onChange={setRatePerMile}
            width="120px"
            value={ratePerMile}
            isNumber
            isCurrency
            maxLength="8"
            disabled={!!proposedCost}
          />
        </Box>
      </Box>
      <Divider sx={{ marginBottom: "16px" }} />
      <Box className="Carrier-modal-input-row">
        <Box>
          <Typography variant="subhead2" color="black_solid.80">
            Weekly
          </Typography>
          <Typography variant="body2" color="black_solid.60">
            Number of times per week
          </Typography>
          <Box sx={{ marginTop: "10px" }}>
            <PrimaryTextField
              label="Weekly"
              onChange={setWeekly}
              width="80px"
              value={weekly}
              isInteger
              maxLength="5"
              disabled={disabledWeekly}
            />
          </Box>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ margin: "0 20px" }} />
        <Box>
          <Typography variant="subhead2" color="black_solid.80">
            Daily
          </Typography>
          <Typography variant="body2" color="black_solid.60">
            Number of times per day
          </Typography>
          <Box sx={{ marginTop: "10px", display: "flex" }}>
            {weeklyInputs.map(input => (
              <Box key={`input-${input.label}`} sx={{ display: "block", marginRight: "16px" }}>
                <PrimaryTextField
                  id={input.label}
                  label={input.label}
                  onChange={input.onChange}
                  width="54px"
                  value={input.value}
                  isInteger
                  maxLength="5"
                  disabled={disabledDaily}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ marginLeft: "auto", marginRight: "20px" }}>
          <SecondaryButton
            label="Cancel"
            onClick={handleCancel}
            disabled={isUpdating}
          />
        </Box>
        <PrimaryButton
          label={action === "edit-capacity" ? "Update" : "Post"}
          onClick={handleSave}
          width="60px"
          isLoading={isUpdating}
          disabled={submitDisabled || isUpdating}
        />
      </Box>
    </Box>
  );
}

PreferredLanesManual.propTypes = {
  action: PropTypes.string,
  userIdMcld: PropTypes.string,
  activeCarrier: PropTypes.object.isRequired,
  existingLane: PropTypes.object,
  handleClose: PropTypes.func.isRequired
};